import React, { useState } from 'react';

import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useSelector } from 'react-redux';
import { getResidentialUnitFilterDataByProject } from '@/services/projects';
import { IState } from '@/state/IState';
import { TranslatableText } from '@/types';
import { useAsyncState } from '@/utils/hooks';
import { MeetingFilterOptions } from '.';
import { FilterButtons, FilterDropDown, FilterPopOut, SingleFilterDropDown, useIsFilterModified } from '@ygtechnologies/bygr-frontend-component-library';

interface MeetingsFilterProps {
    filter: MeetingFilterOptions;
    onFilterChange(f: MeetingFilterOptions): void;
}

type HasSentMeetingOptions = {
    id: MeetingFilterOptions['hasSent'];
    name: TranslatableText;
};
type HasMeetingDateOptions = {
    id: MeetingFilterOptions['hasMeetingDate'];
    name: TranslatableText;
};
type HasConfirmedOptions = {
    id: MeetingFilterOptions['hasConfirmed'];
    name: TranslatableText;
};
type HasRemindedOptions = {
    id: MeetingFilterOptions['hasReminded'];
    name: TranslatableText;
};

const hasSentMeetingOptions: HasSentMeetingOptions[] = [
    { id: 'all', name: <Trans>All</Trans> },
    { id: 'yes', name: <Trans>Yes</Trans> },
    { id: 'no', name: <Trans>No</Trans> },
];

const hasMeetingDateOptions: HasMeetingDateOptions[] = [
    { id: 'all', name: <Trans>All</Trans> },
    { id: 'yes', name: <Trans>Yes</Trans> },
    { id: 'no', name: <Trans>No</Trans> },
];

const hasConfirmedOptions: HasConfirmedOptions[] = [
    { id: 'all', name: <Trans>All</Trans> },
    { id: 'yes', name: <Trans>Yes</Trans> },
    { id: 'no', name: <Trans>No</Trans> },
];
const hasRemindedOptions: HasRemindedOptions[] = [
    { id: 'all', name: <Trans>All</Trans> },
    { id: 'yes', name: <Trans>Yes</Trans> },
    { id: 'no', name: <Trans>No</Trans> },
];

const MeetingsFilter: React.FC<MeetingsFilterProps> = ({ filter, onFilterChange }) => {
    const projectId = useSelector((state: IState) => state.global.activeProject!.id);
    const [isOpen, setIsOpen] = useState(false);
    const [filterData] = useAsyncState(() => getResidentialUnitFilterDataByProject(projectId), [], null); //TODO: fix this so it doesn't reload all the time
    const filterModified = useIsFilterModified(filter, MeetingFilterOptions.empty);

    return (
        <>
            <FilterButtons
                searchText={filter.searchText}
                onSearchTextChange={(v) => onFilterChange({ ...filter, searchText: v })}
                onButtonClick={() => setIsOpen((v) => !v)}
                isActive={filterModified}
            />

            <FilterPopOut
                onClear={() => {
                    onFilterChange(MeetingFilterOptions.empty());
                    setIsOpen(false);
                }}
                onClose={() => setIsOpen(false)}
                isVisible={isOpen}>
                {filterData && (
                    <>
                        <FilterDropDown
                            label={<Trans>Floor</Trans>}
                            placeholder={t`Any floor`}
                            data={filterData.floors.map((f) => ({ id: f, name: f }))!}
                            value={filter.floors}
                            onChange={(v) => onFilterChange({ ...filter, floors: v.map((x) => x.id) })}
                        />

                        <FilterDropDown
                            label={<Trans>Layout types</Trans>}
                            placeholder={t`Any layout type`}
                            data={filterData.layoutTypes.map((f) => ({ id: f, name: f }))!}
                            value={filter.layoutTypes}
                            onChange={(v) => onFilterChange({ ...filter, layoutTypes: v.map((x) => x.id) })}
                        />

                        <FilterDropDown
                            label={<Trans>Unit groups</Trans>}
                            placeholder={t`Any unit groups`}
                            data={filterData.groups.map((f) => ({ id: f.id, name: f.name }))}
                            value={filter.groups}
                            onChange={(v) => onFilterChange({ ...filter, groups: v.map((x) => x.id) })}
                        />
                        <SingleFilterDropDown
                            label={<Trans>Has meeting date</Trans>}
                            placeholder={<Trans>Has meeting date</Trans>}
                            data={hasMeetingDateOptions}
                            value={filter.hasMeetingDate}
                            onChange={(nv) => onFilterChange({ ...filter, hasMeetingDate: nv!.id })}
                        />
                        <SingleFilterDropDown
                            label={<Trans>Has sent invitation</Trans>}
                            placeholder={<Trans>Has sent invitation</Trans>}
                            data={hasSentMeetingOptions}
                            value={filter.hasSent}
                            onChange={(nv) => onFilterChange({ ...filter, hasSent: nv!.id })}
                        />
                        <SingleFilterDropDown
                            label={<Trans>User has confirmed</Trans>}
                            placeholder={<Trans>User has confirmed</Trans>}
                            data={hasConfirmedOptions}
                            value={filter.hasConfirmed}
                            onChange={(nv) => onFilterChange({ ...filter, hasConfirmed: nv!.id })}
                        />
                        <SingleFilterDropDown
                            label={<Trans>Reminder sent</Trans>}
                            placeholder={<Trans>Reminder sent</Trans>}
                            data={hasRemindedOptions}
                            value={filter.hasReminded}
                            onChange={(nv) => onFilterChange({ ...filter, hasReminded: nv!.id })}
                        />
                    </>
                )}
            </FilterPopOut>
        </>
    );
};

export default MeetingsFilter;
